import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import {
  FRAMEWORKS_URL,
  shouldShowFrameworksBanner,
  dismissFrameworksBanner,
} from '../utils/frameworksLink';

/**
 * One-line launch notice for the frameworks platform.
 *
 * Sits in normal flow beneath the fixed navbar's spacer, so it pushes content
 * down without touching the navbar's fixed positioning. Dismissible, and
 * self-expiring via FRAMEWORKS_BANNER_EXPIRES.
 */
export default function FrameworksBanner() {
  // Read storage once on mount rather than every render.
  const [visible, setVisible] = useState(shouldShowFrameworksBanner);

  // The chat page slides the navbar right to clear the sidebar; match it so
  // the banner stays flush with the navbar above it. Mirrors the same
  // `sidebar-state-changed` event Navbar listens for.
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const handleSidebarChange = (e) => setSidebarOpen(e.detail.isOpen);
    window.addEventListener('sidebar-state-changed', handleSidebarChange);
    return () => window.removeEventListener('sidebar-state-changed', handleSidebarChange);
  }, []);

  if (!visible) return null;

  const handleDismiss = () => {
    dismissFrameworksBanner();
    setVisible(false);
  };

  const isChatPage = location.pathname === '/';
  const leftOffset = isChatPage ? (sidebarOpen ? 'ml-64' : 'ml-14') : '';

  return (
    <div
      className={`print:hidden flex-shrink-0 border-b border-indigo-100 bg-indigo-50 transition-[margin] duration-300 ${leftOffset}`}
    >
      <div className="container mx-auto flex items-center gap-3 px-4 py-2">
        <span className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          New
        </span>

        <p className="min-w-0 flex-1 truncate text-sm text-gray-700">
          <span className="font-semibold text-gray-900">Frameworks</span>
          <span className="hidden sm:inline">
            {' '}&mdash; build and manage your CTE program frameworks
          </span>
        </p>

        <a
          href={FRAMEWORKS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-shrink-0 items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
        >
          <span>Open Frameworks</span>
          <ArrowRight size={14} aria-hidden="true" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 rounded p-1 text-gray-500 transition-colors hover:bg-indigo-100 hover:text-gray-700"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
